import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const ADMIN_EMAIL = 'rhinomarch78@gmail.com';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else { setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(userId) {
    try {
      const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();
      if (error) {
        console.log('Profile fetch skipped:', error.message);
        setProfile({ username: 'User', is_admin: false, is_banned: false });
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile({ username: 'User', is_admin: false, is_banned: false });
    } finally {
      setLoading(false);
    }
  }

  const isAdmin = user?.email === ADMIN_EMAIL || profile?.is_admin === true;

  const signUp = async (email, password, username) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    
    if (data.user) {
      try {
        const { error: profileError } = await supabase.from('users').insert({
          id: data.user.id,
          email: email,
          username: username,
          created_at: new Date().toISOString(),
          is_admin: false,
          is_banned: false
        });
        if (profileError) {
          console.error('Profile creation error:', profileError.message);
        }
      } catch (e) {
        console.error('Profile insert failed:', e.message);
      }
    }
    return data;
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        if (error.message?.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password');
        }
        throw error;
      }
      
      if (data?.user) {
        try {
          const { data: userData } = await supabase
            .from('users')
            .select('is_banned')
            .eq('id', data.user.id)
            .single();
          
          if (userData?.is_banned) {
            await supabase.auth.signOut();
            throw new Error('Your account has been suspended.');
          }
        } catch (profileError) {
          console.log('Profile check skipped:', profileError.message);
        }
      }
      
      return data;
    } catch (err) {
      if (err.message?.includes('body stream already read')) {
        throw new Error('Invalid email or password');
      }
      throw err;
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // Ignore signout errors
    }
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, isAdmin, loading, fetchProfile, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
